import { useEffect, useState } from "react";
import { Share, Smartphone, X } from "lucide-react";
import { toast } from "sonner";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

export default function InstallButton({ variant = "pill" }) {
  const [promptEvent, setPromptEvent] = useState(
    () => window.__cgdInstallEvent || null
  );
  const [standalone, setStandalone] = useState(isStandalone);
  const [showHelp, setShowHelp] = useState(false);
  const [isMobile] = useState(() =>
    /android|iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );

  useEffect(() => {
    const onAvailable = () => setPromptEvent(window.__cgdInstallEvent || null);
    const onInstalled = () => {
      setStandalone(true);
      toast.success("CGD installed — see you on the home screen.");
    };
    window.addEventListener("cgd:install-available", onAvailable);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("cgd:install-available", onAvailable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (standalone) return null;
  // Desktop browsers without the native prompt have nothing to install into
  if (!promptEvent && !isMobile) return null;

  const click = () => {
    if (promptEvent) {
      promptEvent.prompt();
      setShowHelp(false);
      return;
    }
    setShowHelp((v) => !v); // iOS / Android without the prompt event
  };

  const help = (
    <div
      data-testid="install-help-popover"
      className="absolute right-0 top-full z-10 mt-3 w-64 rounded-2xl border border-white/12 bg-[#13111A] p-4 text-left shadow-2xl"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cherry">
          Add CGD to your home screen
        </p>
        <button
          data-testid="install-help-close"
          onClick={() => setShowHelp(false)}
          aria-label="Close install help"
          className="text-white/50 transition-colors hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <ol className="mt-3 space-y-2 text-xs leading-relaxed text-white/70">
        <li className="flex items-center gap-2">
          <span className="font-bold text-white">1.</span> Tap the{" "}
          <Share className="h-3.5 w-3.5 shrink-0" /> Share icon in Safari
        </li>
        <li className="flex gap-2">
          <span className="font-bold text-white">2.</span> Choose{" "}
          <span className="font-bold text-white">"Add to Home Screen"</span>
        </li>
      </ol>
      <p className="mt-3 text-[10px] text-white/40">
        Android: open the browser menu and choose "Install app".
      </p>
    </div>
  );

  if (variant === "menu") {
    return (
      <div className="relative mt-2">
        <button
          data-testid="install-app-menu-btn"
          onClick={click}
          className="flex items-center gap-3 font-display text-2xl font-bold uppercase text-white/90"
        >
          <Smartphone className="h-6 w-6 text-cherry" /> Install the app
        </button>
        {showHelp && help}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        data-testid="install-app-btn"
        onClick={click}
        className="hidden items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-white/85 transition-all duration-300 hover:border-cherry hover:text-cherry md:inline-flex"
      >
        <Smartphone className="h-4 w-4" /> Install App
      </button>
      {showHelp && help}
    </div>
  );
}
